package fr.manyverse.app.infrastructure.repository

import fr.manyverse.app.AppLogger
import fr.manyverse.app.domain.model.EventContent
import fr.manyverse.app.domain.model.EventType
import fr.manyverse.app.domain.model.GameEvent
import fr.manyverse.app.domain.repository.GameEventRepository
import fr.manyverse.app.infrastructure.error.mapToDomainException
import fr.manyverse.app.infrastructure.realtime.GameChange
import fr.manyverse.app.infrastructure.realtime.GameRealtimeChannel
import fr.manyverse.app.infrastructure.serialization.appJson
import io.github.jan.supabase.SupabaseClient
import io.github.jan.supabase.postgrest.from
import io.github.jan.supabase.postgrest.query.Columns
import io.github.jan.supabase.postgrest.query.Order
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.mapNotNull
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive
import kotlin.time.Instant

private const val TAG = "GameEventRepository"
private const val EVENTS_TABLE = "game_table_events"

class SupabaseGameEventRepository(
    private val supabaseClient: SupabaseClient,
    private val gameChannel: GameRealtimeChannel
) : GameEventRepository {

    private val json = appJson {
        ignoreUnknownKeys = true
        encodeDefaults = true
    }

    override suspend fun createEvent(
        tableId: String,
        type: EventType,
        triggeredBy: String,
        targetParticipants: List<String>,
        content: EventContent
    ): Result<GameEvent> {
        return try {
            val contentJson = json.encodeToString(EventContent.serializer(), content)

            val insertDto = GameEventInsertDto(
                tableId = tableId,
                type = type.name,
                triggeredBy = triggeredBy,
                targetParticipants = targetParticipants,
                content = contentJson
            )

            val event = supabaseClient
                .from(EVENTS_TABLE)
                .insert(insertDto) {
                    select()
                }
                .decodeSingle<GameEventDto>()

            Result.success(event.toDomain())
        } catch (e: Exception) {
            AppLogger.warn(TAG, "Failed to create event for table=$tableId")
            Result.failure(mapToDomainException(e, "table"))
        }
    }

    override suspend fun getTableEvents(
        tableId: String,
        limit: Int,
        offset: Int,
        eventTypes: List<EventType>,
        since: Instant?
    ): Result<List<GameEvent>> {
        return try {
            val events = supabaseClient
                .from(EVENTS_TABLE)
                .select(Columns.ALL) {
                    filter {
                        eq("table_id", tableId)
                        if (eventTypes.isNotEmpty()) {
                            isIn("type", eventTypes.map { it.name })
                        }
                        if (since != null) {
                            gt("created_at", since.toString())
                        }
                    }
                    order("created_at", Order.DESCENDING)
                    range(offset.toLong(), (offset + limit - 1).toLong())
                }
                .decodeList<GameEventDto>()

            Result.success(events.map { it.toDomain() })
        } catch (e: Exception) {
            AppLogger.warn(TAG, "Failed to get events for table=$tableId")
            Result.failure(mapToDomainException(e, "table"))
        }
    }

    override fun observeNewEvents(
        tableId: String
    ): Flow<GameEvent> = gameChannel.changes(EVENTS_TABLE).mapNotNull { change ->
        val record = change.record ?: return@mapNotNull null
        if (change.type != GameChange.Type.INSERT || extractString(record["table_id"]) != tableId) {
            return@mapNotNull null
        }
        try {
            parseEventFromRecord(record)
        } catch (e: Exception) {
            AppLogger.error(TAG, "Failed to parse event from real-time update", e)
            null
        }
    }

    private fun parseEventFromRecord(record: Map<String, Any?>): GameEvent? {
        val id = extractString(record["id"]) ?: return null
        val tableId = extractString(record["table_id"]) ?: return null
        val type = extractString(record["type"]) ?: return null
        val triggeredBy = extractString(record["triggered_by"]) ?: return null
        val createdAt = extractString(record["created_at"]) ?: return null

        val targetParticipants = extractStringList(json, record["target_participants"])

        val contentRaw = record["content"] ?: return null
        val content = try {
            val contentStr = when (contentRaw) {
                is JsonPrimitive -> contentRaw.content
                is JsonObject -> contentRaw.toString()
                is String -> contentRaw
                else -> contentRaw.toString()
            }
            json.decodeFromString<EventContent>(contentStr)
        } catch (e: Exception) {
            AppLogger.error(TAG, "Failed to parse event content (${contentRaw::class.simpleName})", e)
            return null
        }

        return GameEvent(
            id = id,
            tableId = tableId,
            type = type,
            triggeredBy = triggeredBy,
            targetParticipants = targetParticipants,
            content = content,
            createdAt = Instant.parse(createdAt)
        )
    }

    @Serializable
    private data class GameEventInsertDto(
        @SerialName("table_id")
        val tableId: String,

        @SerialName("type")
        val type: String,

        @SerialName("triggered_by")
        val triggeredBy: String,

        @SerialName("target_participants")
        val targetParticipants: List<String>,

        @SerialName("content")
        val content: String
    )

    @Serializable
    private data class GameEventDto(
        @SerialName("id")
        val id: String,

        @SerialName("table_id")
        val tableId: String,

        @SerialName("type")
        val type: String,

        @SerialName("triggered_by")
        val triggeredBy: String,

        @SerialName("target_participants")
        val targetParticipants: List<String>,

        @SerialName("content")
        val content: String,

        @SerialName("created_at")
        val createdAt: Instant
    ) {
        fun toDomain(): GameEvent {
            val json = appJson {
                ignoreUnknownKeys = true
                encodeDefaults = true
            }
            val eventContent = json.decodeFromString<EventContent>(content)
            return GameEvent(
                id = id,
                tableId = tableId,
                type = type,
                triggeredBy = triggeredBy,
                targetParticipants = targetParticipants,
                content = eventContent,
                createdAt = createdAt
            )
        }
    }
}
